import React, { useState } from 'react';
import { Box, Container, Typography, IconButton, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoDarkIcon from '@/asset/logo_dark_icon_blue_accent_transparent.png';
import LogoLightIcon from '@/asset/logo_light_icon_transparent.png';
import Button from '../ui/Button';
import Drawer from '../ui/Drawer';

export const HeaderNav: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isDark = theme.palette.mode === 'dark';

  const navLogo = isDark ? LogoDarkIcon : LogoLightIcon;

  const navItems = [
    { label: 'About Us', href: '/about' },
    { label: 'Capabilities', href: '/capabilities' },
    { label: 'Projects', href: '/projects' },
    { label: 'Industries', href: '/industries' },
    { label: 'Insights', href: '/insights' },
    { label: 'Careers', href: '/careers' },
  ];

  const handleNavigate = (href: string) => {
    if (href.startsWith('/#')) {
      const elementId = href.substring(2);
      if (location.pathname === '/' || location.pathname === '/home') {
        const elem = document.getElementById(elementId);
        if (elem) {
          elem.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      navigate('/');
      setTimeout(() => {
        document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backgroundColor: isDark ? 'rgba(11, 15, 25, 0.85)' : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Logo + Title -> Navigates to / */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src={navLogo}
                alt="Inverika Logo"
                sx={{ height: { xs: 44, md: 52 }, width: 'auto', objectFit: 'contain' }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Bank Gothic MD BT', 'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  letterSpacing: '0.01em',
                  fontSize: { xs: '1.25rem', md: '1.6rem' },
                }}
              >
                Inverika
              </Typography>
            </Box>

            {/* Desktop Nav Links (Default Inter Font) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Typography
                  key={item.label}
                  onClick={() => handleNavigate(item.href)}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: '0.9375rem',
                    transition: 'color 0.3s ease',
                    '&:hover': { color: theme.palette.primary.main },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* Desktop CTA Button */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Button
                variant="contained"
                glow
                onClick={() => handleNavigate('/#contact')}
              >
                Discuss Your Project
              </Button>
            </Box>

            {/* Mobile Hamburger Button */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ display: { xs: 'block', md: 'none' }, color: theme.palette.text.primary }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        navItems={navItems}
        onNavigate={handleNavigate}
      />
    </>
  );
};

export default HeaderNav;
